import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import React, { useState, useEffect, useRef } from "react";
import { SETTINGS_TABS } from "../content/settings/settingsRegistry";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import TuneIcon from "@mui/icons-material/Tune";
import HomeIcon from "@mui/icons-material/Home";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import DisplaySettingsIcon from "@mui/icons-material/DisplaySettings";
import PunchClockIcon from "@mui/icons-material/PunchClock";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleIcon from "@mui/icons-material/People";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import ChatIcon from "@mui/icons-material/Chat";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import FolderIcon from "@mui/icons-material/Folder";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SportsIcon from "@mui/icons-material/Sports";
import HandymanIcon from "@mui/icons-material/Handyman";
import ComputerIcon from "@mui/icons-material/Computer";
import CampaignIcon from "@mui/icons-material/Campaign";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RateReviewIcon from "@mui/icons-material/RateReview";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import ArticleIcon from "@mui/icons-material/Article";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import GroupsIcon from "@mui/icons-material/Groups";
import SchoolIcon from "@mui/icons-material/School";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { analytics } from "../../utilities/requests";
import Account from "../content/home/top_bar_components/Account";


const navBackgroundColor = "#111827";
const iconWidth = 57;
const navItemHeight = 44;
const subnavItemHeight = 36;
const expandedDrawerWidth = 265;
const logoContainerHeight = 64;
const sectionTabs = {
  employees: [
    "employeesInformation",
    "companyTeam",
  ],
  operations: [
    "inventory",
    "liveDayLabor",
    "plan",
    "logbook",
    "logbookReporting",
    "maintenance",
    "boards",
    "headcountPlanning",
    "contests",
    "deployment",
  ],
  reporting: [
    "overview",
    "dashboard",
    "reporting",
    "employeeReporting",
    "guestReviews",
    "prebuiltReports",
  ],
  training: [
    "takeTrainingEmployee",
    "trainingOverview",
    "createTrainingContent",
  ],
  timePayroll: ["payroll", "payrollEmployee", "tips", "tipsEmployee"],
  configurations: [
    "onboardingCenter",
    "logbookConfiguration",
    "departmentRoleStationSettings",
    "trainingProficiency",
    "locationsIntegrationsHoursSettings",
    "checkInTemplate",
    "billingSettings",
    "internalBillingOverview",
  ],
};

const getSectionForTab = (tab) =>
  Object.entries(sectionTabs).find(([, tabs]) => tabs.includes(tab))?.[0] ??
  null;

export default function NavigationSideBar(props) {
  const [shouldExpand, setShouldExpand] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const hoverTimerRef = useRef(null);

  const handleMouseEnter = React.useCallback(() => {
    hoverTimerRef.current = setTimeout(() => {
      setShouldExpand(true);
    }, 500); // delay
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setShouldExpand(false);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setOpenSection(getSectionForTab(props.currentTab));
  }, [props.currentTab]);

  const drawerWidth = shouldExpand ? expandedDrawerWidth : 60;
  const toggleSection = (section) => {
    setOpenSection((currentSection) =>
      currentSection === section ? null : section
    );
  };

  const enabledTicketTypes = [
    props.maintenanceEnabled,
    props.itTicketsEnabled,
    props.marketingTicketsEnabled,
    props.menuTicketsEnabled,
  ].filter(Boolean);
  const ticketIcon = (() => {
    if (enabledTicketTypes.length !== 1) {
      return enabledTicketTypes.length > 1 ? ConfirmationNumberIcon : HandymanIcon;
    }
    if (props.itTicketsEnabled) return ComputerIcon;
    if (props.marketingTicketsEnabled) return CampaignIcon;
    if (props.menuTicketsEnabled) return RestaurantMenuIcon;
    return HandymanIcon;
  })();
  const ticketTitle = (() => {
    if (enabledTicketTypes.length !== 1) return enabledTicketTypes.length > 1 ? "Tickets" : "Maintenance";
    if (props.itTicketsEnabled) return "IT";
    if (props.marketingTicketsEnabled) return "Marketing";
    if (props.menuTicketsEnabled) return "Menu";
    return "Maintenance";
  })();

  const sidebarItems = [
    {
      icon: HomeIcon,
      title: "Schedule",
      tab: "home",
      roles: ["rightwork_internal", "company_admin", "company_manager"],
      requiresScheduling: true,
    },
    {
      icon: HomeIcon,
      title: "Home",
      tab: "employeeHome",
      roles: ["company_employee"],
      requiresScheduling: true,
    },
    {
      icon: PendingActionsIcon,
      title: "Approvals",
      tab: "actions",
      roles: ["rightwork_internal", "company_admin", "company_manager"],
      enabled: "approvalsEnabled",
      requiresScheduling: true,
    },
    {
      icon: ChatIcon,
      title: "Chat",
      tab: "chat",
      roles: ["all"],
      enabled: "chatEnabled",
    },
    {
      icon: EventAvailableIcon,
      title: "Published Schedule",
      tab: "publishedSchedule",
      roles: ["all"],
      enabled: "publishedScheduleEnabled",
      requiresScheduling: true,
    },
    {
      icon: PeopleIcon,
      title: "Employees",
      section: "employees",
      subtabs: [
        {
          icon: PeopleIcon,
          title: "Team Members",
          tab: "employeesInformation",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          enabled: "employeesEnabled",
        },
        {
          icon: GroupsIcon,
          title: "Company Members",
          tab: "companyTeam",
          roles: ["rightwork_internal", "company_admin"],
        },
      ],
      roles: ["rightwork_internal", "company_admin", "company_manager"],
    },
    {
      icon: AssignmentIndIcon,
      title: "Operations",
      section: "operations",
      subtabs: [
        {
          icon: Inventory2OutlinedIcon,
          title: "Inventory",
          tab: "inventory",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          // Toast stock is internal-only; Prep plan shows once a company has prep items.
          enabledAny: ["inventoryAvailable", "prepPlanAvailable"],
        },
        {
          icon: PlayCircleOutlineIcon,
          title: "Live Action",
          tab: "liveDayLabor",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          enabled: "liveLaborEnabled",
          requiresScheduling: true,
        },
        {
          icon: AssignmentIndIcon,
          title: "Tasks",
          tab: "plan",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          enabled: "dailyPlanEnabled",
          requiresScheduling: true,
        },
        {
          icon: MenuBookIcon,
          title: "Logbook",
          tab: "logbook",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          overrides: ["logbook_access"],
          enabled: "logbookEnabled",
        },
        {
          icon: ticketIcon,
          title: ticketTitle,
          tab: "maintenance",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          overrides: ["maintenance_access", "it_access", "marketing_access", "menu_access"],
          enabledAny: ["maintenanceEnabled", "itTicketsEnabled", "marketingTicketsEnabled", "menuTicketsEnabled"],
        },
        {
          icon: SpaceDashboardIcon,
          title: "Planning Board",
          tab: "boards",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          enabled: "boardsEnabled",
        },
        {
          icon: GroupsIcon,
          title: "Headcount Planning",
          tab: "headcountPlanning",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          enabled: "headcountPlanningEnabled",
          requiresScheduling: true,
        },
        {
          icon: EmojiEventsIcon,
          title: "Contests",
          tab: "contests",
          roles: ["all"],
          enabled: "contestsEnabled",
        },
        {
          icon: SportsIcon,
          title: "Deployment",
          tab: "deployment",
          roles: ["rightwork_internal"],
          enabled: "deploymentEnabled",
          requiresScheduling: true,
        },
      ],
      roles: ["all"],
      overrides: ["logbook_access", "maintenance_access", "it_access", "marketing_access", "menu_access"],
    },
    {
      icon: AssessmentIcon,
      title: "Reporting",
      section: "reporting",
      subtabs: [
        {
          icon: DashboardIcon,
          title: "Dashboard",
          tab: "dashboard",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          enabled: "dashboardEnabled",
          requiresScheduling: true,
        },
        {
          icon: AnalyticsIcon,
          title: "Overview",
          tab: "overview",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          enabled: "overviewEnabled",
          requiresScheduling: true,
        },
        {
          icon: StorefrontIcon,
          title: "Analytics",
          tab: "reporting",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          enabled: "reportingEnabled",
          requiresScheduling: true,
        },
        {
          icon: RateReviewIcon,
          title: "Guest Feedback",
          tab: "guestReviews",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          enabled: "guestReviewsEnabled",
          requiresScheduling: true,
        },
        {
          icon: ArticleIcon,
          title: "Report Library",
          tab: "prebuiltReports",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          enabled: "reportingEnabled",
          requiresScheduling: true,
        },
      ],
      roles: ["rightwork_internal", "company_admin", "company_manager"],
      enabledAny: ["overviewEnabled", "dashboardEnabled", "reportingEnabled", "guestReviewsEnabled"],
    },
    {
      icon: SchoolIcon,
      title: "Training",
      section: "training",
      subtabs: [
        {
          icon: LocalLibraryIcon,
          title: "My Training",
          tab: "takeTrainingEmployee",
          roles: ["all"],
          enabled: "trainingEnabled",
        },
        {
          icon: AssessmentIcon,
          title: "Training Overview",
          tab: "trainingOverview",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          enabled: "trainingEnabled",
        },
        {
          icon: AddCircleOutlineIcon,
          title: "Training Content",
          tab: "createTrainingContent",
          roles: ["rightwork_internal", "company_admin"],
          enabled: "trainingEnabled",
        },
      ],
      roles: ["all"],
    },
    {
      // Surveys, polls and swipe decks shared by public link. Same assets as
      // Training Content, different purpose; admin-only in v1.
      icon: CampaignOutlinedIcon,
      title: "Engagement",
      tab: "engagement",
      roles: ["rightwork_internal", "company_admin"],
      enabled: "engagementEnabled",
    },
    {
      icon: FolderIcon,
      title: "Company Files",
      tab: "knowledgeHub",
      roles: [
        "rightwork_internal",
        "company_admin",
        "company_manager",
        "company_employee",
      ],
      enabledAny: ["documentsEnabled", "assistantEnabled"],
    },
    {
      icon: PunchClockIcon,
      title: "Time & Payroll",
      section: "timePayroll",
      requiresScheduling: true,
      subtabs: [
        {
          icon: PunchClockIcon,
          title: "Time Entries",
          tab: "payroll",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          enabled: "payrollEnabled",
          requiresScheduling: true,
        },
        {
          icon: PunchClockIcon,
          title: "Time Entries",
          tab: "payrollEmployee",
          roles: ["company_employee"],
          enabled: "payrollEnabled",
          requiresScheduling: true,
        },
        {
          icon: AttachMoneyIcon,
          title: "Tips",
          tab: "tips",
          roles: ["rightwork_internal", "company_admin", "company_manager"],
          enabled: "tipPoolingEnabled",
        },
        {
          icon: AttachMoneyIcon,
          title: "Tips",
          tab: "tipsEmployee",
          roles: ["company_employee"],
          enabled: "tipPoolingEnabled",
        },
      ],
      roles: ["all"],
    },
    {
      icon: TuneIcon,
      title: "Labor Model",
      tab: "laborModel",
      roles: ["rightwork_internal", "company_admin"],
      enabled: "laborModelEnabled",
      requiresScheduling: true,
    },
    {
      icon: DisplaySettingsIcon,
      title: "Settings",
      tab: "settings",
      roles: ["rightwork_internal", "company_admin", "company_manager"],
      overrides: ["billing_access", "onboarding_access"],
    },
    {
      icon: PersonIcon,
      title: "User Profile",
      tab: "userProfileEmployee",
      roles: ["company_employee"],
    },
  ];

  const setCurrentTabWrapper = (tabValue) => {
    analytics.track("sidebar_item_clicked", props.homeStore, {
      sidebar_item: tabValue,
    });
    props.setCurrentTab(tabValue);
  };

  const getNotificationInfo = (item) => {
    if (item.tab === "actions" && props.approvalBadgeCount > 0) {
      return {
        hasNotifications: true,
        notificationCount: props.approvalBadgeCount > 99 ? "99+" : props.approvalBadgeCount
      };
    }
    if (item.tab === "chat" && props.streamChatUnreadCount > 0) {
      return {
        hasNotifications: true,
        notificationCount: props.streamChatUnreadCount > 99 ? "99+" : props.streamChatUnreadCount
      };
    }
    return {
      hasNotifications: false,
      notificationCount: 0
    };
  };

  const isNavItemRoleAllowed = (item) => {
    const roles = item?.roles ?? [];
    const overrides = item?.overrides ?? [];
    const permissionOverrides = props.permissionOverrides ?? [];

    return (
      roles.includes(props.permissionRole) ||
      roles.includes("all") ||
      overrides.some((override) => permissionOverrides.includes(override))
    );
  };

  const isNavItemFeatureEnabled = (item) => {
    if (item?.enabledAll) {
      return item.enabledAll.every((flag) => props[flag]);
    }
    if (item?.enabledAny) {
      return item.enabledAny.some((flag) => props[flag]);
    }
    return !item?.enabled || props[item.enabled];
  };

  // A section that needs scheduling still shows when one of its subtabs
  // does not (Tips has its own flag), and then shows only those subtabs.
  const isNavItemSchedulingAllowed = (item) =>
    !item?.requiresScheduling
    || props.schedulingEnabled !== false
    || (item?.subtabs || []).some((subtab) => !subtab.requiresScheduling && isNavItemRoleAllowed(subtab) && isNavItemFeatureEnabled(subtab));

  const isNavItemVisible = (item) =>
    isNavItemRoleAllowed(item) &&
    isNavItemFeatureEnabled(item) &&
    isNavItemSchedulingAllowed(item);

  const getVisibleSubtabs = (item) =>
    item?.subtabs?.filter((subtab) => isNavItemVisible(subtab)) ?? [];

  // When scheduling is off, training is the store's primary surface (the
  // Schedule/Approvals/Time & Payroll nav is hidden, and on mobile Home becomes
  // the training overview), so float the Training section to the top of the
  // sidebar instead of leaving it mid-list. Order is unchanged when scheduling
  // is on. Harmless when training is disabled — the section renders null.
  const orderedSidebarItems =
    props.schedulingEnabled === false
      ? [
          ...sidebarItems.filter((item) => item.section === "training"),
          ...sidebarItems.filter((item) => item.section !== "training"),
        ]
      : sidebarItems;

  const renderSidebarItem = (item) => {
    if (isNavItemVisible(item)) {
      if (item.subtabs) {
        const visibleSubtabs = getVisibleSubtabs(item);
        if (visibleSubtabs.length === 0) {
          return null;
        }

        const isSelected = visibleSubtabs.some(
          (subtab) => subtab.tab === props.currentTab
        );
        const isOpen = item.section === openSection;
        return (
          <React.Fragment key={item.title}>
            <ListItem
              button
              onClick={() => toggleSection(item.section)}
              sx={{
                height: `${navItemHeight}px`,
                backgroundColor: isSelected ? "#2f3340" : "transparent",
                "&:hover": {
                  backgroundColor: "#2f3340",
                },
                display: "flex",
                alignItems: "center",
                padding: 0,
              }}
            >
              <ListItemIcon
                sx={{
                  color: "#DCDCDC",
                  minWidth: iconWidth,
                  width: iconWidth,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <item.icon />
              </ListItemIcon>
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  fontSize: "0.95rem",
                }}
                sx={{
                  opacity: shouldExpand ? 1 : 0,
                  transition: "opacity 0.3s",
                  marginLeft: 1,
                  overflow: "hidden",
                  width: shouldExpand ? "225px" : "0px",
                  whiteSpace: "nowrap",
                }}
              />
              {shouldExpand && (
                <Box sx={{ marginLeft: "auto", marginRight: 1 }}>
                  {isOpen ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </Box>
              )}
            </ListItem>
            {shouldExpand && (
              <Collapse
                in={isOpen}
                timeout="auto"
                unmountOnExit
              >
                <List
                  component="div"
                  disablePadding
                  sx={{
                    pb: 0.5,
                    pt: 0.5,
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: "10px",
                      bottom: "8px",
                      left: `${iconWidth - 24}px`,
                      width: "1px",
                      backgroundColor: "rgba(220, 220, 220, 0.12)",
                      pointerEvents: "none",
                      zIndex: 1,
                    },
                  }}
                >
                  {visibleSubtabs.map((subtab) => {
                    const SubtabIcon = subtab.icon;
                    return (
                      <ListItem
                        button
                        key={subtab.tab}
                        data-help-id={`sidebar-${subtab.tab}`}
                        onClick={() => setCurrentTabWrapper(subtab.tab)}
                        sx={{
                          height: `${subnavItemHeight}px`,
                          backgroundColor: "transparent",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                          "&:hover .subtab-content": {
                            backgroundColor: "#3f4350",
                          },
                          paddingLeft: `${iconWidth - 12}px`,
                          paddingRight: "12px",
                        }}
                      >
                        <Box
                          className="subtab-content"
                          sx={{
                            alignItems: "center",
                            backgroundColor:
                              props.currentTab === subtab.tab
                                ? "#3f4350"
                                : "transparent",
                            borderRadius: "6px",
                            display: "flex",
                            height: "32px",
                            overflow: "hidden",
                            px: "8px",
                            pr: "14px",
                            transition: "background-color 0.2s",
                            width: "100%",
                          }}
                        >
                          {SubtabIcon && (
                            <ListItemIcon
                              sx={{
                                color: "#C9CDD6",
                                minWidth: 40,
                                width: 40,
                                display: "flex",
                                justifyContent: "center",
                              }}
                            >
                              <SubtabIcon fontSize="small" />
                            </ListItemIcon>
                          )}
                          <ListItemText
                            primary={subtab.title}
                            primaryTypographyProps={{
                              fontSize: "0.88rem",
                              noWrap: true,
                            }}
                            sx={{
                              flex: "1 1 auto",
                              minWidth: 0,
                              opacity: shouldExpand ? 1 : 0,
                              transition: "opacity 0.3s",
                              marginLeft: SubtabIcon ? 0 : 1,
                              overflow: "hidden",
                              width: shouldExpand ? "100%" : "0px",
                              whiteSpace: "nowrap",
                            }}
                          />
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        );
      } else {
        const { hasNotifications, notificationCount } = getNotificationInfo(item);

        return (
          <ListItem
            button
            key={item.tab}
            data-help-id={`sidebar-${item.tab}`}
            onClick={() => setCurrentTabWrapper(item.tab)}
            sx={{
              height: `${navItemHeight}px`,
              backgroundColor:
                (props.currentTab === item.tab || (item.tab === "settings" && SETTINGS_TABS.includes(props.currentTab))) ? "#2f3340" : "transparent",
              "&:hover": {
                backgroundColor: "#2f3340",
              },
              display: "flex",
              alignItems: "center",
              padding: 0,
            }}
          >
            <ListItemIcon
              sx={{
                color: "#DCDCDC",
                minWidth: iconWidth,
                width: iconWidth,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {(hasNotifications && !shouldExpand) ? (
                <Badge
                  badgeContent={notificationCount}
                  color="error"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.70rem",
                      width: "18px",
                      height: "18px",
                      backgroundColor: "#e11d48",
                      color: "white",
                      fontWeight: "bold",
                      // Position the badge slightly to the right when collapsed
                      right: 0,
                      top: 0,
                    },
                  }}
                >
                  <item.icon />
                </Badge>
              ) : (
                <item.icon />
              )}
            </ListItemIcon>
            <ListItemText
              primary={item.title}
              primaryTypographyProps={{
                fontSize: "0.95rem",
              }}
              sx={{
                opacity: shouldExpand ? 1 : 0,
                transition: "opacity 0.3s",
                marginLeft: 1,
                overflow: "hidden",
                width: shouldExpand ? "225px" : "0px",
                whiteSpace: "nowrap",
              }}
            />
            {/* Show notification count as text when expanded */}
            {shouldExpand && hasNotifications && (
              <Box
                sx={{
                  marginLeft: "auto",
                  marginRight: 2,
                  backgroundColor: "#e11d48",
                  color: "white",
                  borderRadius: "12px",
                  width: "22px",
                  height: "22px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: "bold",
                  flexShrink: 0
                }}
              >
                {notificationCount}
              </Box>
            )}
          </ListItem>
        );
      }
    }
    return null;
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          backgroundColor: navBackgroundColor,
          color: "#DCDCDC",
          transition: "width 0.3s ease-in-out",
          overflowX: "hidden",
          overflowY: "hidden",
          display: "flex",
          flexDirection: "column",
          msOverflowStyle: "none",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        },
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: logoContainerHeight,
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {shouldExpand ? (
          <img
            src={process.env.PUBLIC_URL + "/images/logo_gray_new.png"}
            alt="Rightwork logo"
            style={{
              height: "32px",
              marginBottom: "10px",
              marginTop: "20px",
              minWidth: "150px",
            }}
          />
        ) : (
          <img
            src={process.env.PUBLIC_URL + "/images/grayicon.png"}
            alt="Rightwork icon"
            style={{
              height: "36px",
              marginBottom: "10px",
              marginTop: "20px",
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          msOverflowStyle: "none",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <List sx={{ py: 0.75 }}>{orderedSidebarItems?.map(renderSidebarItem)}</List>
      </Box>
      {/* Account menu pinned to the bottom of the rail. Lives here (rather than
          a per-tab top bar) so account/logout access is global across every
          tab. Account is redux-connected for logout and only needs username. */}
      {props.username && (
        <Box
          sx={{
            flexShrink: 0,
            borderTop: "1px solid rgba(220, 220, 220, 0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: shouldExpand ? "flex-start" : "center",
            px: shouldExpand ? "16px" : 0,
            py: "12px",
          }}
        >
          <Account variant="sidebar" showLabel={shouldExpand} />
        </Box>
      )}
    </Drawer>
  );
}
